import React, { useState, useEffect, useRef } from 'react';
import { useHistory } from 'react-router-dom';
import { observer } from 'mobx-react';
import mixpanel from 'mixpanel-browser';
import { isMobile, isIOS, isAndroid } from 'react-device-detect';
import IosInstructions from './IosInstructions';
import { toDataURL } from 'qrcode';

// Import podcast app icons
import applepodcasts from '../../../Assets/podcast-apps/icon-applepodcasts.svg';
import spotify from '../../../Assets/podcast-apps/Spotify_icon.svg';
import googlepodcasts from '../../../Assets/podcast-apps/icon-googlepodcasts.svg';
import overcast from '../../../Assets/podcast-apps/icon-overcast.svg';
import pocketcasts from '../../../Assets/podcast-apps/icon-pocketcasts.svg';
import castro from '../../../Assets/podcast-apps/icon-castro.svg';
import radiopublic from '../../../Assets/podcast-apps/icon-radiopublic.svg';
import downcast from '../../../Assets/podcast-apps/downcast.png';

const PodcastSetup = observer(({ userFeedUrl }) => {
  const [app, setApp] = useState('');
  const [device, setDevice] = useState(null);
  const history = useHistory();
  
  // Refs for podcast app buttons
  const applePodcastsRef = useRef(null);
  const overcastRef = useRef(null);
  const pocketCastsRef = useRef(null);
  const googlePodcastsRef = useRef(null);
  const castroRef = useRef(null);
  const radioPublicRef = useRef(null);
  const downcastRef = useRef(null);
  const [qrCodeUrl, setQrCodeUrl] = useState('');

  useEffect(() => {
    if (isIOS) {
      setDevice('ios');
    } else if (isAndroid) {
      setDevice('android');
    } else {
      setDevice('desktop');
    }
  }, []);

  useEffect(() => {
    if (userFeedUrl) {
      try {
        // Add event listeners for podcast app buttons
        applePodcastsRef.current?.addEventListener('click', () => {
          document.location.href = `podcast://${userFeedUrl.replace('https://', '')}`;
        });

        googlePodcastsRef.current?.addEventListener('click', () => {
          document.location.href = `googlepodcasts://subscribe-by-rss-feed?feed=${btoa(userFeedUrl)}`;
        });

        overcastRef.current?.addEventListener('click', () => {
          document.location.href = `overcast://x-callback-url/add?url=${encodeURIComponent(userFeedUrl)}`;
        });

        pocketCastsRef.current?.addEventListener('click', () => {
          alert("Episodes may take a few hours to show up. For a faster experience, use Apple Podcasts, Overcast, Google Podcasts, or another podcast app.");
          document.location.href = `pktc://subscribe/${userFeedUrl.replace('https://', '')}`;
        });

        downcastRef.current?.addEventListener('click', () => {
          document.location.href = `downcast://${userFeedUrl.replace('https://', '')}`;
        });

        castroRef.current?.addEventListener('click', () => {
          navigator.clipboard.writeText(userFeedUrl);
          document.location.href = `castros://subscribe/${userFeedUrl.replace('https://', '')}`;
        });

        radioPublicRef.current?.addEventListener('click', () => {
          document.location.href = `https://radiopublic.com/${encodeURIComponent(userFeedUrl)}`;
        });
      } catch (err) {
        console.error('Error setting up podcast app listeners:', err);
      }
    }
  }, [userFeedUrl]);

  useEffect(() => {
    if (device === 'desktop' && userFeedUrl) {
      const qrUrl = `${process.env.REACT_APP_FRONTEND_URL}/setupnew?feed_url=${encodeURIComponent(userFeedUrl)}`;
      toDataURL(qrUrl)
        .then(setQrCodeUrl)
        .catch(console.error);
    }
  }, [device, userFeedUrl]);

  const handleDone = (completed = true) => {
    mixpanel.track("Quickstart: Podcast setup done", {
      completed,
      device: device || 'unknown'
    });
    mixpanel.people.set_once({
      podcast_app_setup: completed
    });
    history.push('/add');
  };

  const renderIosSetup = () => (
    <>
      <h1 className="headline" style={{ marginBottom: '0px' }}>
        Select your podcast player
      </h1>
      <div className="subheadline" style={{ marginTop: '0px' }}>
        This will open the app and subscribe to your podcast. If you're not sure, try Apple Podcasts.
      </div>

      <IosInstructions app={app} feedUrl={userFeedUrl} />
      <div className="podcast-app-container">
        <a
          ref={applePodcastsRef}
          className="transparent-button mobile"
        >
          <img src={applepodcasts} alt="Apple Podcasts" />
          Apple Podcasts (Recommended)
        </a>
        <a
          className="transparent-button mobile"
          onClick={() => setApp('Spotify')}
        >
          <img src={spotify} alt="Spotify" />
          Spotify
        </a>
        <a
          ref={googlePodcastsRef}
          className="transparent-button mobile"
        >
          <img src={googlepodcasts} alt="Google Podcasts" />
          Google Podcasts
        </a>
        <a
          ref={overcastRef}
          className="transparent-button mobile"
        >
          <img src={overcast} alt="Overcast" />
          Overcast
        </a>
        <a
          ref={pocketCastsRef}
          className="transparent-button mobile"
        >
          <img src={pocketcasts} alt="Pocket Casts" />
          Pocket Casts
        </a>
        <a
          ref={castroRef}
          className="transparent-button mobile"
        >
          <img src={castro} alt="Castro" />
          Castro
        </a>
        <a
          ref={radioPublicRef}
          className="transparent-button mobile"
        >
          <img src={radiopublic} alt="Radio Public" />
          Radio Public
        </a>
        <a
          ref={downcastRef}
          className="transparent-button mobile"
        >
          <img src={downcast} alt="Downcast" />
          Downcast
        </a>
      </div>
    </>
  );

  const renderAndroidSetup = () => (
    <>
      <h1 className="headline" style={{ marginBottom: '0px' }}>
        Select your podcast player
      </h1>
      <p>If you're not sure, try Google Podcasts</p>
      
      <div style={{ margin: "5px 0 15px 0" }}>
        <IosInstructions app={app} feedUrl={userFeedUrl} />
        <a 
          className="transparent-button mobile" 
          onClick={() => setApp('Spotify')}
          style={{
            display: 'flex',
            alignItems: 'center',
            border: "2px solid white",
            justifyContent: 'center'
          }}
        >
          <img src={spotify} alt="Spotify" />
          Spotify
        </a>

        <a 
          className="transparent-button mobile" 
          href={`https://subscribeonandroid.com/${userFeedUrl.replace("https://", "")}`} 
          target="_blank"
          style={{
            display: 'flex',
            alignItems: 'center',
            border: "2px solid white",
            justifyContent: 'center'
          }}
        >
          Other Podcast Players
        </a>
      </div>
    </>
  );

  const renderDesktopSetup = () => (
    <>
      <h1 className="headline" style={{ marginBottom: '0px' }}>
        <span role="img" aria-label="headphones">🎧</span> Listen in your favorite podcast app
      </h1>
      <div className="subheadline" style={{ marginTop: '0px' }}>
        Grab your phone, then scan this QR code to subscribe to your personal podcast:
      </div>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', marginTop: 32 }}>
        {qrCodeUrl && (
          <img src={qrCodeUrl} alt="QR code for podcast feed" style={{ width: 240, height: 240, marginRight: 32, borderRadius: 8, background: '#fff' }} />
        )}
        <div style={{ color: 'white', fontSize: 20, marginLeft: 16 }}>
          <b>How to scan:</b>
          <ol style={{ margin: '12px 0 0 16px', fontSize: 16 }}>
            <li>Open the Camera or QR scanner app.</li>
            <li>Point it at the QR code for 3 seconds.</li>
            <li>Click the popup that appears.</li>
          </ol>
        </div>
      </div>
    </>
  );

  return (
    <div className="center-static">
      <div style={{ display: 'flex', gap: '10px', marginBottom: '20px' }}>
        <button 
          className="transparent-button"
          style={device === 'ios' ? {color: 'pink', border: "2px solid pink"} : null}
          onClick={() => setDevice('ios')}
        >
          iOS
        </button>
        <button 
          className="transparent-button"
          style={device === 'android' ? {color: 'pink', border: "2px solid pink"} : null}
          onClick={() => setDevice('android')}
        >
          Android
        </button>
        <button
          className="transparent-button"
          style={device === 'desktop' ? {color: 'pink', border: "2px solid pink"} : null}
          onClick={() => setDevice('desktop')}
        >
          Desktop
        </button>
      </div>
      {device === 'ios' ? renderIosSetup() : device === 'android' ? renderAndroidSetup() : renderDesktopSetup()}
      <div className="cta-div" style={{ position: 'static', marginTop: '24px' }}>
        <button 
          className="cta-button" 
          style={{ width: '270px' }}
          onClick={() => handleDone(true)}
        >
          Done
        </button>
        <a 
          href="#" 
          onClick={(e) => {
            e.preventDefault();
            handleDone(false);
          }} 
          className="sub-cta-link" 
          style={{ fontSize: '16px' }}
        >
          I'll do this later
        </a>
      </div>
    </div>
  );
});

export default PodcastSetup; 